import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { LAYOUT } from '../theme/tokens';

/**
 * "Powered by [logo] Talogent" — the vendor attribution line.
 *
 * THE MARK IS INLINE SVG, DELIBERATELY
 * ------------------------------------
 * It is a copy of talogent.com's own `favicon.svg`: a 32×32 rounded square in the brand
 * sage (`#8A9A8B`) carrying a white bold "T". Reproduced as markup rather than fetched,
 * for the same reason icon fonts are banned in this package — a client container may be
 * offline or air-gapped, and a logo that 404s reads as a broken product. It also means
 * no request leaves the install just to render a static mark.
 *
 * The site draws its own wordmark as text rather than shipping a logo image (its bundle
 * contains no logo asset), so the wordmark here is text too, matching that treatment.
 *
 * If Talogent publishes a proper logo file later, replace the <svg> below and nothing
 * else changes.
 */

export interface PoweredByTalogentProps {
  /** Aligns the row. Defaults to centre, which suits a page footer. */
  align?: 'left' | 'center';
}

/** Brand sage, from talogent.com's favicon. */
const TALOGENT_SAGE = '#8A9A8B';

export function PoweredByTalogent({ align = 'center' }: PoweredByTalogentProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'center' ? 'center' : 'flex-start',
        gap: 1,
      }}
    >
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Powered by</Typography>

      <Box
        component="svg"
        viewBox="0 0 32 32"
        role="img"
        aria-label="Talogent"
        sx={{ width: 20, height: 20, display: 'block', flexShrink: 0 }}
      >
        <rect width="32" height="32" rx="8" fill={TALOGENT_SAGE} />
        <text
          x="16"
          y="23"
          textAnchor="middle"
          fill="#fff"
          fontFamily="system-ui, sans-serif"
          fontWeight="800"
          fontSize="20"
        >
          T
        </text>
      </Box>

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', letterSpacing: '0.01em' }}>
        Talogent
      </Typography>
    </Box>
  );
}


/**
 * The anchored footer BAR — the credit pinned to the foot of the viewport.
 *
 * Extracted so the shell and the pre-auth screens (sign-in, forced password change)
 * share one definition. Those screens live outside AppShell, so without this they simply
 * had no footer, which is what the owner spotted after the first pass.
 *
 * `left` is the shell's drawer offset on desktop so the bar starts after the nav; the
 * pre-auth screens have no drawer and pass nothing, so it spans the full width.
 */
export interface TalogentFooterBarProps {
  /** Left inset on desktop — the shell passes its drawer width. */
  insetLeft?: number;
  versionLabel?: string;
}

export function TalogentFooterBar({ insetLeft = 0, versionLabel }: TalogentFooterBarProps) {
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        left: { xs: 0, md: `${insetLeft}px` },
        height: LAYOUT.footerHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Opaque, with a hairline above: several screens size themselves from `100vh`,
        // so content can reach this band however much padding is reserved for it. A
        // solid strip means the worst case is a shorter visible table, never text on text.
        bgcolor: 'background.default',
        borderTop: '1px solid',
        borderColor: 'divider',
        pointerEvents: 'none',
        zIndex: (t) => t.zIndex.drawer - 1,
      }}
    >
      <PoweredByTalogent />
      {versionLabel ? (
        <Box
          sx={{
            position: 'absolute',
            right: 2,
            display: { xs: 'none', sm: 'block' },
            pointerEvents: 'auto',
          }}
        >
          <Typography
            title={versionLabel}
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              userSelect: 'text',
              whiteSpace: 'nowrap',
            }}
          >
            Version {versionLabel}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
